import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  useDisclosure,
  useToast,
  Spinner,
  Icon,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react';
import { FiExternalLink, FiRefreshCw, FiEye, FiEyeOff, FiChevronRight, FiSearch, FiPlus, FiCopy } from 'react-icons/fi';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
  Divider,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import api from '../services/api';
import {
  fetchZapmailMailboxCredentials,
  getOAuthAuthUrl,
} from '../services/zapmailService';
import { openOAuthInNewTab } from '../utils/openOAuthInNewTab';
import {
  consumeOAuthFlowStarted,
  publishOAuthResult,
  subscribeOAuthResult,
} from '../utils/oauthCrossTab';
import OAuthTabCompleteCard from '../components/OAuthTabCompleteCard';
import TotpQrCode from '../components/TotpQrCode';

// Helper function to parse UTC timestamp from backend
// Backend sends UTC datetime without 'Z' suffix, so we need to add it
const parseUTCDate = (dateString) => {
  if (!dateString) return null;
  // If the date string doesn't end with 'Z' and doesn't have timezone info, add 'Z'
  const str = String(dateString);
  if (!str.endsWith('Z') && !str.includes('+') && !str.includes('-', 10)) {
    return new Date(str + 'Z');
  }
  return new Date(str);
};

// Official Smartlead logo from https://cdn.prod.website-files.com/6204a703ff61516512c04f55/6618e20081f3fd598cbaabcc_Smartlead%20Logo.svg
const SmartleadLogo = ({ size = "36px" }) => (
  <Box
    as="svg"
    viewBox="0 0 40 40"
    w={size}
    h={size}
  >
    {/* Purple rounded square background */}
    <path 
      d="M0 5.03C0 2.25 2.25 0 5.03 0H34.97C37.75 0 40 2.25 40 5.03V34.97C40 37.75 37.75 40 34.97 40H5.03C2.25 40 0 37.75 0 34.97V5.03Z" 
      fill="#6E58F1"
    />
    {/* White icon shapes */}
    <path 
      d="M5.77 16.92C5.77 13.47 8.56 10.67 12 10.67H12.35C13.81 10.67 14.99 11.86 14.99 13.32V20.53C14.99 21.98 13.81 23.17 12.35 23.17H12C8.56 23.17 5.77 20.37 5.77 16.92Z" 
      fill="white"
    />
    <path 
      d="M16.41 11.78C16.41 11.11 16.8 10.5 17.41 10.23L26.47 6.15C27.59 5.65 28.85 6.47 28.85 7.7V26.27C28.85 27.52 27.57 28.34 26.45 27.82L17.39 23.63C16.79 23.35 16.41 22.75 16.41 22.08V21.22C16.41 20.52 16.84 19.88 17.5 19.63L22.55 17.72C23.28 17.44 23.28 16.41 22.55 16.13L17.5 14.22C16.84 13.97 16.41 13.33 16.41 12.63V11.78Z" 
      fill="white"
    />
    <path 
      d="M34.22 18.41V18.03C34.22 15.94 32.53 14.24 30.45 14.24C30.34 14.24 30.26 14.33 30.26 14.43V22.01C30.26 22.11 30.34 22.2 30.45 22.2C32.53 22.2 34.22 20.5 34.22 18.41Z" 
      fill="white"
    />
    <path 
      d="M12 25.58C12 25.21 12.29 24.91 12.66 24.91H16.17C16.54 24.91 16.84 25.21 16.84 25.58V31.57C16.84 32.91 15.76 34 14.42 34C13.08 34 12 32.91 12 31.57V25.58Z" 
      fill="white"
    />
  </Box>
);

// HeyReach logo - extracted icon portion from official SVG
const HeyReachLogo = ({ size = "36px" }) => (
  <Box
    as="svg"
    viewBox="0 0 210 154.62"
    w={size}
    h={size}
  >
    <defs>
      <linearGradient id="heyreach-gradient-1" x1="36.23" y1="68.19" x2="79.75" y2="68.19" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#ff2832"/>
        <stop offset="1" stopColor="#fff"/>
      </linearGradient>
      <linearGradient id="heyreach-gradient-2" x1="12.65" y1="85.52" x2="84.19" y2="85.52" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#ffbc00"/>
        <stop offset="1" stopColor="#fff"/>
      </linearGradient>
      <linearGradient id="heyreach-gradient-3" x1="27.39" y1="99.25" x2="89.93" y2="99.25" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#78ba3a"/>
        <stop offset="1" stopColor="#fff"/>
      </linearGradient>
      <linearGradient id="heyreach-gradient-4" x1="6.87" y1="116.06" x2="97.46" y2="116.06" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#37b2ff"/>
        <stop offset="1" stopColor="#fff"/>
      </linearGradient>
      <linearGradient id="heyreach-gradient-5" x1="57.5" y1="126.2" x2="108.07" y2="126.2" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#388eff"/>
        <stop offset="1" stopColor="#fff"/>
      </linearGradient>
    </defs>
    <g>
      {/* Hand outline */}
      <path d="M201.62,58.75l-.03-.29-.06-.29c-.76-3.93-4.59-7.1-9.5-7.89-.87-.14-1.76-.21-2.65-.21-1.91,0-3.81.31-5.65.93-4.43,1.49-8.48,4.55-12.35,9.36-1.12,1.38-2.21,2.87-3.24,4.45-3.97-7.64-10.95-23.73-12.6-44.94-.52-7.02-6.49-12.51-13.58-12.51-.17,0-.35,0-.52.01h-.07c-2.67.13-5.17,1.01-7.28,2.51-.79-2.44-2.2-4.66-4.14-6.37l-.07-.06c-2.59-2.23-5.87-3.45-9.23-3.45-.52,0-1.06.03-1.52.08-7.45.78-13.02,7.2-12.7,14.63.02.6.05,1.19.07,1.78-2.05-1.14-4.33-1.74-6.65-1.74-.96,0-1.92.1-2.82.29-7.34,1.48-12.19,8.45-11.04,15.88.42,2.67.88,5.28,1.34,7.7-1.38-.46-2.84-.7-4.35-.7-.95,0-1.91.1-2.82.29-6.79,1.41-11.51,7.71-10.98,14.67l.02.18c.08.77.18,1.38.28,1.92l-30.93,5.62c-4.97.91-8.72,5.05-9.14,10.1-.09,1.09-.02,2.17.2,3.17.27,1.31.77,2.53,1.46,3.63l-16.08,2.92c-5.82,1.05-9.82,6.47-9.1,12.35l.02.14c.73,5.53,5.49,9.7,11.07,9.7.61,0,1.22-.05,1.87-.16l1.91-.35c-.14.7-.22,1.42-.22,2.16,0,.6.04,1.18.13,1.73.24,1.52.79,2.92,1.57,4.17l-13.02,2.37c-2.99.53-5.58,2.22-7.3,4.76-1.7,2.53-2.3,5.56-1.67,8.53l.06.25c1.16,5.02,5.73,8.66,10.88,8.66.59,0,1.2-.05,1.85-.15h.08s37.74-6.88,37.74-6.88c-.08.39-.14.79-.18,1.19-.09,1.02-.04,2.05.16,3.06,1,5.28,5.61,9.11,10.98,9.11.61,0,1.22-.05,1.87-.16l42.19-7.68c1.34,1.23,2.66,2.33,3.93,3.31l.08.08.04.03.19.18.3.18,1.53,1.16h.21c8.61,5.58,18.21,8.52,27.89,8.52h0c4.55,0,9.1-.65,13.52-1.93,9.94-2.87,17.19-7.77,22.8-15.42,6.67-9.1,10.97-22.14,13.13-39.86.84-6.95,4.1-12.15,6.97-16.73,2.92-4.62,5.69-8.99,5.15-13.97Z"/>
      {/* Hand details in white */}
      <path fill="#fff" d="M138.14,64.24c-6.78-15.89-9.02-30.66-9.69-40.9v-.13c-.23-2.92-.41-6.02-.54-9.29-.09-2.02-.99-3.91-2.51-5.25-1.57-1.35-3.54-1.97-5.56-1.74-3.82.4-6.73,3.72-6.55,7.54.85,27.34,9.42,48.48,11.71,53.69,4.18-1.84,8.62-3.1,13.15-3.9Z"/>
      <path fill="#fff" d="M194.79,59.48c-.13-.67-1.35-2.02-3.86-2.42-1.66-.27-3.41-.09-5.02.45-3.73,1.25-6.78,4.17-9.2,7.18-1.17,1.44-2.25,2.96-3.24,4.53-.4.67-.8,1.35-1.21,2.02-.49.9-.85,1.93-1.52,2.69-.54.72-1.39.99-2.2,1.31-.8.32-1.61.67-2.38,1.08-1.52.8-2.96,1.75-4.35,2.78-3.72,2.87-6.82,6.69-8.66,11.04-.9,3.19-5.3,2.16-4.58-1.03.63-2.29,1.66-4.44,2.83-6.5,2.83-4.99,7.05-9.16,11.99-12.16-3.95-7.05-12.75-25.14-14.68-50.05-.27-3.59-3.37-6.29-7-6.15-1.84.09-3.5.85-4.72,2.2-1.21,1.35-1.84,3.05-1.75,4.85.49,9.97,2.56,25.22,9.79,41.61.09.18.14.36.18.54,1.44-.09,2.87-.13,4.31-.13,2.91-.05,3.19,4.17.31,4.53-5.47.36-10.95,1.39-16.11,3.28-4.94,1.84-9.69,4.26-13.64,7.67-3.5,3.46-8.35-1.93-4.49-5.07,1.13-.86,2.29-1.66,3.46-2.38-1.35-2.91-5.03-11.53-8.03-24.01,0-.04-.05-.09-.05-.14-1.52-5.88-2.92-12.43-4.13-19.61-.36-1.93-1.39-3.55-3.01-4.67-1.61-1.12-3.5-1.52-5.43-1.12-3.77.76-6.2,4.31-5.61,8.08,1.57,9.97,3.73,19.03,4.14,20.7.04.04.04.09.04.13.05.09.05.14.05.14,1.88,6.87,4.71,16.07,8.61,25,.76,1.75-.04,3.72-1.75,4.49-1.74.76-3.72-.04-4.49-1.75-4.58-10.45-7.77-21.32-9.65-28.55-.94-3.59-4.53-5.83-8.17-5.07-3.45.71-5.79,3.94-5.52,7.41.13,1.22.33,1.91.51,2.79,4.31,20.74,14.53,58.5,37.72,76.12t.01,0s.03.02.05.03c11.36,7.99,24.65,10.5,37.35,6.82,8.53-2.47,14.46-6.46,19.17-12.88,5.92-8.08,9.83-20.06,11.85-36.63,1.03-8.48,4.89-14.63,7.99-19.57,2.47-3.91,4.4-7,4.13-9.56ZM168.71,103.65c-.31,2.74-.94,5.52-1.93,8.21-5.21,15.17-22.98,20.87-37.03,14.54-4.41-1.96-8.37-4.86-11.34-8.62-.97.99-1.93,1.99-2.85,3.01-2.92,3.01-7.27-1.48-4.08-4.31,4.04-3.23,8.21-6.1,12.61-8.75.85-.49,1.93-.22,2.42.63.45.76.27,1.71-.4,2.29-1.39,1.18-2.76,2.4-4.1,3.65,6.18,6,15.38,9.13,23.76,7.4,10.23-1.89,15.44-9.61,18.22-19.03.4-1.26,1.75-1.98,3.05-1.57,1.12.36,1.8,1.44,1.66,2.56Z"/>
      {/* Colorful gradient lines */}
      <path fill="url(#heyreach-gradient-1)" d="M79.75,68.84l-38.43,7c-.25.04-.52.07-.78.07-2.03,0-3.8-1.44-4.21-3.43-.08-.38-.11-.8-.08-1.24.16-1.94,1.61-3.53,3.52-3.88l37.92-6.89c.6,2.63,1.28,5.44,2.06,8.37Z"/>
      <path fill="url(#heyreach-gradient-2)" d="M84.19,83.56l-66.46,12.1c-.25.04-.52.07-.78.07-2.16,0-3.99-1.61-4.27-3.75v-.05c-.28-2.27,1.25-4.35,3.5-4.76l65.37-11.88c.8,2.7,1.68,5.47,2.64,8.26Z"/>
      <path fill="url(#heyreach-gradient-3)" d="M89.93,98.06l-57.45,10.45c-.25.04-.52.07-.78.07-2.12,0-3.94-1.58-4.26-3.67-.03-.21-.05-.43-.05-.65,0-2.08,1.49-3.86,3.54-4.23l55.58-10.1c1.05,2.72,2.18,5.45,3.41,8.14Z"/>
      <path fill="url(#heyreach-gradient-4)" d="M97.46,112.21l-85.49,15.56c-.26.04-.52.07-.78.07-1.99,0-3.74-1.39-4.19-3.33l-.03-.12c-.24-1.14,0-2.31.65-3.28.66-.98,1.66-1.63,2.81-1.83l82.52-15c1.41,2.72,2.91,5.37,4.52,7.93Z"/>
      <path fill="url(#heyreach-gradient-5)" d="M108.07,125.81l-45.49,8.28c-.25.04-.52.07-.78.07-2.07,0-3.85-1.48-4.23-3.53-.08-.38-.1-.77-.06-1.15.17-1.93,1.61-3.52,3.52-3.87l40.55-7.37c2.01,2.69,4.17,5.23,6.48,7.57Z"/>
    </g>
  </Box>
);

// Official Gmail envelope SVG (google-gmail-svgrepo-com)
const GmailLogo = ({ size = "28px" }) => (
  <Box as="svg" viewBox="0 -31.5 256 256" w={size} h={size} flexShrink={0} preserveAspectRatio="xMidYMid">
    <path d="M58.1818182,192.049515 L58.1818182,93.1404244 L27.5066233,65.0770089 L0,49.5040608 L0,174.59497 C0,184.253152 7.82545455,192.049515 17.4545455,192.049515 L58.1818182,192.049515 Z" fill="#4285F4"/>
    <path d="M197.818182,192.049515 L238.545455,192.049515 C248.203636,192.049515 256,184.224061 256,174.59497 L256,49.5040608 L224.844415,67.3422767 L197.818182,93.1404244 L197.818182,192.049515 Z" fill="#34A853"/>
    <polygon fill="#EA4335" points="58.1818182 93.1404244 54.0077618 54.4932827 58.1818182 17.5040608 128 69.8676972 197.818182 17.5040608 202.487488 52.4960089 197.818182 93.1404244 128 145.504061"/>
    <path d="M197.818182,17.5040608 L197.818182,93.1404244 L256,49.5040608 L256,26.2313335 C256,4.64587897 231.36,-7.65957557 214.109091,5.28587897 L197.818182,17.5040608 Z" fill="#FBBC04"/>
    <path d="M0,49.5040608 L26.7588051,69.5731646 L58.1818182,93.1404244 L58.1818182,17.5040608 L41.8909091,5.28587897 C24.6109091,-7.65957557 0,4.64587897 0,26.2313335 L0,49.5040608 Z" fill="#C5221F"/>
  </Box>
);


// Microsoft Outlook logo (ms-outlook-svgrepo-com)
const OutlookLogo = ({ size = "28px" }) => {
  const id = 'ipol';
  return (
    <Box as="svg" viewBox="0 0 32 32" w={size} h={size} flexShrink={0} preserveAspectRatio="xMidYMid" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={`${id}g0`} x1="10" y1="16" x2="30" y2="16" gradientUnits="userSpaceOnUse">
          <stop stopColor="#064484" />
          <stop offset="1" stopColor="#0F65B5" />
        </linearGradient>
        <linearGradient id={`${id}g1`} x1="8" y1="26.7692" x2="32" y2="26.7692" gradientUnits="userSpaceOnUse">
          <stop stopColor="#1B366F" />
          <stop offset="1" stopColor="#2657B0" />
        </linearGradient>
        <linearGradient id={`${id}g2`} x1="32" y1="23" x2="8" y2="23" gradientUnits="userSpaceOnUse">
          <stop stopColor="#44DCFD" />
          <stop offset="0.453125" stopColor="#259ED0" />
        </linearGradient>
        <linearGradient id={`${id}g3`} x1="8" y1="23" x2="32" y2="23" gradientUnits="userSpaceOnUse">
          <stop stopColor="#259ED0" />
          <stop offset="1" stopColor="#44DCFD" />
        </linearGradient>
        <linearGradient id={`${id}g4`} x1="0" y1="16" x2="18" y2="16" gradientUnits="userSpaceOnUse">
          <stop stopColor="#064484" />
          <stop offset="1" stopColor="#0F65B5" />
        </linearGradient>
        <mask id={`${id}mask`} style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="8" y="14" width="24" height="16">
          <path d="M8 14H30C31.1046 14 32 14.8954 32 16V28C32 29.1046 31.1046 30 30 30H10C8.89543 30 8 29.1046 8 28V14Z" fill={`url(#${id}g1)`} />
        </mask>
      </defs>
      <rect x="10" y="2" width="20" height="28" rx="2" fill={`url(#${id}g0)`} />
      <rect x="10" y="5"  width="10" height="10" fill="#32A9E7" />
      <rect x="20" y="5"  width="10" height="10" fill="#58D9FD" />
      <rect x="10" y="15" width="10" height="10" fill="#167EB4" />
      <rect x="20" y="15" width="10" height="10" fill="#32A9E7" />
      <g mask={`url(#${id}mask)`}>
        <path d="M32 14V18H30V14H32Z" fill="#135298" />
        <path d="M32 30V16L7 30H32Z"  fill={`url(#${id}g2)`} />
        <path d="M8 30V16L33 30H8Z"   fill={`url(#${id}g3)`} />
      </g>
      <path d="M8 12C8 10.3431 9.34315 9 11 9H17C18.6569 9 20 10.3431 20 12V24C20 25.6569 18.6569 27 17 27H8V12Z" fill="#000000" fillOpacity="0.3" />
      <rect y="7" width="18" height="18" rx="2" fill={`url(#${id}g4)`} />
      <path d="M14 16.0693V15.903C14 13.0222 11.9272 11 9.01582 11C6.08861 11 4 13.036 4 15.9307V16.097C4 18.9778 6.07278 21 9 21C11.9114 21 14 18.964 14 16.0693ZM11.6424 16.097C11.6424 18.0083 10.5665 19.1579 9.01582 19.1579C7.46519 19.1579 6.37342 17.9806 6.37342 16.0693V15.903C6.37342 13.9917 7.44937 12.8421 9 12.8421C10.5348 12.8421 11.6424 14.0194 11.6424 15.9307V16.097Z" fill="white" />
    </Box>
  );
};


const ZapmailPasswordField = ({ label, value, toast }) => {
  const [visible, setVisible] = useState(false);
  if (!value) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: `${label} copied`, status: 'success', duration: 2000, isClosable: true });
    } catch {
      toast({ title: 'Could not copy', status: 'error', duration: 2000, isClosable: true });
    }
  };
  return (
    <FormControl size="sm" mt={3}>
      <FormLabel mb={1} fontSize="xs" color="#a3a3a3">
        {label}
      </FormLabel>
      <InputGroup size="sm">
        <Input
          readOnly
          type={visible ? 'text' : 'password'}
          value={value}
          bg="#171717"
          borderColor="rgba(255,255,255,0.12)"
          color="#fafafa"
          fontFamily="mono"
          fontSize="sm"
        />
        <InputRightElement width="4.5rem">
          <HStack spacing={0}>
            <IconButton
              aria-label={visible ? 'Hide' : 'Show'}
              size="xs"
              variant="ghost"
              color="#a3a3a3"
              icon={visible ? <FiEyeOff /> : <FiEye />}
              onClick={() => setVisible((v) => !v)}
            />
            <IconButton
              aria-label="Copy"
              size="xs"
              variant="ghost"
              color="#a3a3a3"
              icon={<FiCopy />}
              onClick={copy}
            />
          </HStack>
        </InputRightElement>
      </InputGroup>
    </FormControl>
  );
};


const ConnectedEmailAccountRow = ({
  account,
  logo,
  provider,
  onDisconnect,
  disconnectLoading,
  onGetPassword,
  passwordLoadingEmail,
}) => {
  const showPassword = account.is_zapmail_provisioned || account.mailbox_source === 'zapmail';
  const connectedAtFormatted = account.connected_at
    ? parseUTCDate(account.connected_at)?.toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
        timeZone: 'America/Denver', timeZoneName: 'short',
      })
    : null;
  return (
    <HStack
      px={4}
      py={3}
      borderRadius="10px"
      bg="#171717"
      border="1px solid rgba(255,255,255,0.10)"
      justify="space-between"
      align="flex-start"
      flexWrap="wrap"
      gap={2}
    >
      <HStack spacing={3} minW={0} align="flex-start">
        {logo}
        <VStack align="start" spacing={0.5} minW={0}>
          <Text fontSize="xs" color="#fafafa" fontFamily="mono" wordBreak="break-all">
            {account.email}
          </Text>
          {connectedAtFormatted && (
            <Text fontSize="10px" color="#666" lineHeight="1.4">
              Connected {connectedAtFormatted}
            </Text>
          )}
        </VStack>
      </HStack>
      <HStack flexShrink={0}>
        {showPassword && (
          <Button
            size="xs"
            bg="transparent"
            color="rgba(255,255,255,0.75)"
            borderRadius="full"
            fontWeight="500"
            fontSize="12px"
            px={3}
            h="28px"
            border="1px solid rgba(255,255,255,0.15)"
            _hover={{ borderColor: 'rgba(255,255,255,0.3)', color: '#fafafa', bg: 'rgba(255,255,255,0.05)' }}
            _focus={{ boxShadow: 'none', outline: 'none' }}
            onClick={() => onGetPassword(provider, account.email)}
            isLoading={passwordLoadingEmail === account.email}
            loadingText="..."
          >
            Get password
          </Button>
        )}
        <Button
          size="xs"
          bg="transparent"
          color="rgba(239,68,68,0.65)"
          borderRadius="full"
          fontWeight="500"
          fontSize="12px"
          px={3}
          h="28px"
          border="1px solid rgba(239,68,68,0.25)"
          _hover={{ borderColor: 'rgba(239,68,68,0.45)', color: 'rgba(239,68,68,0.9)', bg: 'rgba(239,68,68,0.06)' }}
          _focus={{ boxShadow: 'none', outline: 'none' }}
          onClick={() => onDisconnect(account.email)}
          isLoading={disconnectLoading}
          loadingText="..."
        >
          Disconnect
        </Button>
      </HStack>
    </HStack>
  );
};


const IntegrationsPage = () => {
  // Smartlead state
  const [smartleadStatus, setSmartleadStatus] = useState(null);
  const [isSmartleadConnecting, setIsSmartleadConnecting] = useState(false);
  const [smartleadApiKey, setSmartleadApiKey] = useState('');
  const [showSmartleadApiKey, setShowSmartleadApiKey] = useState(false);
  const { isOpen: isSmartleadDrawerOpen, onOpen: onSmartleadDrawerOpen, onClose: onSmartleadDrawerClose } = useDisclosure();

  // HeyReach state
  const [heyreachStatus, setHeyreachStatus] = useState(null);
  const [isHeyreachConnecting, setIsHeyreachConnecting] = useState(false);
  const [heyreachApiKey, setHeyreachApiKey] = useState('');
  const [showHeyreachApiKey, setShowHeyreachApiKey] = useState(false);
  const { isOpen: isHeyreachDrawerOpen, onOpen: onHeyreachDrawerOpen, onClose: onHeyreachDrawerClose } = useDisclosure();

  // Gmail state
  const [gmailStatus, setGmailStatus] = useState(null);
  const [isGmailConnecting, setIsGmailConnecting] = useState(false);

  // Outlook state
  const [outlookStatus, setOutlookStatus] = useState(null);
  const [isOutlookConnecting, setIsOutlookConnecting] = useState(false);

  // Gmail drawer state
  const { isOpen: isGmailDrawerOpen, onOpen: onGmailDrawerOpen, onClose: onGmailDrawerClose } = useDisclosure();

  // Outlook drawer state
  const { isOpen: isOutlookDrawerOpen, onOpen: onOutlookDrawerOpen, onClose: onOutlookDrawerClose } = useDisclosure();

  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [zapmailCredsModal, setZapmailCredsModal] = useState(null);
  const [zapmailPasswordLoadingEmail, setZapmailPasswordLoadingEmail] = useState(null);
  const [oauthReturnUi, setOauthReturnUi] = useState(null);
  const toast = useToast();

  useEffect(() => {
    fetchAllStatuses();

    const params = new URLSearchParams(window.location.search);
    const gmailOk = params.get('gmail_connected') === 'true';
    const gmailErr = params.get('gmail_error') === 'true';
    const outlookOk = params.get('outlook_connected') === 'true';
    const outlookErr = params.get('outlook_error') === 'true';
    if (!gmailOk && !gmailErr && !outlookOk && !outlookErr) return;

    const oauthProvider = gmailOk || gmailErr ? 'gmail' : 'outlook';
    const oauthOk = gmailOk || outlookOk;
    const flow = consumeOAuthFlowStarted();
    publishOAuthResult({ provider: oauthProvider, ok: oauthOk });
    window.history.replaceState({}, '', window.location.pathname);

    if (flow) {
      setOauthReturnUi({
        provider: oauthProvider,
        ok: oauthOk,
        accountEmail: flow.accountEmail,
      });
    } else if (oauthOk) {
      setTimeout(() => {
        toast({
          title: oauthProvider === 'gmail' ? 'Gmail connected' : 'Outlook connected',
          description: 'Your account has been linked successfully.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }, 400);
    } else {
      toast({
        title: oauthProvider === 'gmail' ? 'Gmail connection failed' : 'Outlook connection failed',
        description: 'Something went wrong. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
    fetchAllStatuses();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return subscribeOAuthResult(({ provider, ok }) => {
      const label = provider === 'gmail' ? 'Gmail' : 'Outlook';
      toast({
        title: ok ? `${label} connected` : `${label} connection failed`,
        description: ok
          ? 'Continue on this tab—the sign-in tab may be closed.'
          : 'Please try again.',
        status: ok ? 'success' : 'error',
        duration: 6000,
        isClosable: true,
      });
      fetchAllStatuses();
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllStatuses = async () => {
    setIsLoading(true);
    await Promise.all([
      fetchSmartleadStatus(),
      fetchHeyreachStatus(),
      fetchGmailStatus(),
      fetchOutlookStatus(),
    ]);
    setIsLoading(false);
  };

  const fetchSmartleadStatus = async () => {
    try {
      const { data } = await api.get('/api/integrations/smartlead/status');
      setSmartleadStatus(data);
    } catch (error) {
      console.error('Error fetching Smartlead status:', error);
      setSmartleadStatus({ status: 'disconnected' });
    }
  };

  const fetchHeyreachStatus = async () => {
    try {
      const { data } = await api.get('/api/integrations/heyreach/status');
      setHeyreachStatus(data);
    } catch (error) {
      console.error('Error fetching HeyReach status:', error);
      setHeyreachStatus({ status: 'disconnected' });
    }
  };

  const fetchGmailStatus = async () => {
    try {
      const { data } = await api.get('/api/integrations/gmail/status');
      // API now returns { accounts: [{email, connected_at}, ...] }
      setGmailStatus(data);
    } catch (error) {
      console.error('Error fetching Gmail status:', error);
      setGmailStatus({ accounts: [] });
    }
  };

  const fetchOutlookStatus = async () => {
    try {
      const { data } = await api.get('/api/integrations/microsoft/status');
      setOutlookStatus(data);
    } catch (error) {
      console.error('Error fetching Outlook status:', error);
      setOutlookStatus({ accounts: [] });
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Smartlead Handlers
  // ─────────────────────────────────────────────────────────────────────────

  const handleSmartleadConnect = async () => {
    if (!smartleadApiKey.trim()) {
      toast({
        title: 'API Key Required',
        description: 'Please enter your Smartlead API key',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSmartleadConnecting(true);
      const { data } = await api.post('/api/integrations/smartlead/connect', {
        api_key: smartleadApiKey.trim()
      });

      if (data.success) {
        toast({
          title: 'Connected!',
          description: 'Smartlead integration connected successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setSmartleadStatus({
          status: 'active',
          connected_at: data.connected_at,
          last_verified_at: data.connected_at
        });
        setSmartleadApiKey('');
        setShowSmartleadApiKey(false);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to connect to Smartlead';
      toast({
        title: 'Connection Failed',
        description: message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSmartleadConnecting(false);
    }
  };

  const handleSmartleadDisconnect = async () => {
    try {
      setIsSmartleadConnecting(true);
      await api.delete('/api/integrations/smartlead/disconnect');
      
      toast({
        title: 'Disconnected',
        description: 'Smartlead integration has been disconnected',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      
      setSmartleadStatus({ status: 'disconnected' });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to disconnect Smartlead',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSmartleadConnecting(false);
    }
  };

  const handleSmartleadVerify = async () => {
    try {
      setIsSmartleadConnecting(true);
      const { data } = await api.post('/api/integrations/smartlead/verify');
      
      if (data.valid) {
        toast({
          title: 'Verified!',
          description: 'Smartlead API key is valid',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setSmartleadStatus(prev => ({
          ...prev,
          status: 'active',
          last_verified_at: data.last_verified_at
        }));
      } else {
        toast({
          title: 'Invalid Key',
          description: data.message || 'Your Smartlead API key is no longer valid',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        setSmartleadStatus(prev => ({
          ...prev,
          status: 'invalid'
        }));
      }
    } catch (error) {
      toast({
        title: 'Verification Failed',
        description: error.response?.data?.detail || 'Could not verify connection',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSmartleadConnecting(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // HeyReach Handlers
  // ─────────────────────────────────────────────────────────────────────────

  const handleHeyreachConnect = async () => {
    if (!heyreachApiKey.trim()) {
      toast({
        title: 'API Key Required',
        description: 'Please enter your HeyReach API key',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsHeyreachConnecting(true);
      const { data } = await api.post('/api/integrations/heyreach/connect', {
        api_key: heyreachApiKey.trim()
      });

      if (data.success) {
        toast({
          title: 'Connected!',
          description: 'HeyReach integration connected successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setHeyreachStatus({
          status: 'active',
          connected_at: data.connected_at,
          last_verified_at: data.connected_at
        });
        setHeyreachApiKey('');
        setShowHeyreachApiKey(false);
        // Dispatch event for other components to refresh
        window.dispatchEvent(new Event('heyreachIntegrationChanged'));
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to connect to HeyReach';
      toast({
        title: 'Connection Failed',
        description: message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsHeyreachConnecting(false);
    }
  };

  const handleHeyreachDisconnect = async () => {
    try {
      setIsHeyreachConnecting(true);
      await api.delete('/api/integrations/heyreach/disconnect');
      
      toast({
        title: 'Disconnected',
        description: 'HeyReach integration has been disconnected',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      
      setHeyreachStatus({ status: 'disconnected' });
      // Dispatch event for other components to refresh
      window.dispatchEvent(new Event('heyreachIntegrationChanged'));
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to disconnect HeyReach',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsHeyreachConnecting(false);
    }
  };

  const handleHeyreachVerify = async () => {
    try {
      setIsHeyreachConnecting(true);
      const { data } = await api.post('/api/integrations/heyreach/verify');
      
      if (data.valid) {
        toast({
          title: 'Verified!',
          description: 'HeyReach API key is valid',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setHeyreachStatus(prev => ({
          ...prev,
          status: 'active',
          last_verified_at: data.last_verified_at
        }));
      } else {
        toast({
          title: 'Invalid Key',
          description: data.message || 'Your HeyReach API key is no longer valid',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        setHeyreachStatus(prev => ({
          ...prev,
          status: 'invalid'
        }));
      }
    } catch (error) {
      toast({
        title: 'Verification Failed',
        description: error.response?.data?.detail || 'Could not verify connection',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsHeyreachConnecting(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Gmail Handlers
  // ─────────────────────────────────────────────────────────────────────────

  const handleConnectGmail = async () => {
    try {
      setIsGmailConnecting(true);
      const { data } = await api.get('/api/integrations/gmail/auth-url');
      openOAuthInNewTab(data.auth_url, toast, { provider: 'gmail' });
    } catch (error) {
      toast({
        title: 'Connection failed',
        description: error.response?.data?.detail || 'Could not initiate Gmail connection. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsGmailConnecting(false);
    }
  };

  const handleDisconnectGmail = async (email) => {
    try {
      setIsGmailConnecting(true);
      await api.delete(`/api/integrations/gmail/disconnect?email=${encodeURIComponent(email)}`);
      setGmailStatus(prev => ({
        accounts: (prev?.accounts ?? []).filter(a => a.email !== email),
      }));
      window.dispatchEvent(new Event('gmailIntegrationChanged'));
      toast({
        title: 'Gmail disconnected',
        description: `${email} has been unlinked.`,
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to disconnect Gmail. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsGmailConnecting(false);
    }
  };

  const handleGetZapmailPassword = async (provider, email) => {
    setZapmailPasswordLoadingEmail(email);
    try {
      const data = await fetchZapmailMailboxCredentials(provider, email);
      setZapmailCredsModal(data);
    } catch (error) {
      toast({
        title: 'Could not load password',
        description: error.response?.data?.detail || error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setZapmailPasswordLoadingEmail(null);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Outlook Handlers
  // ─────────────────────────────────────────────────────────────────────────

  const handleConnectZapmailOutlook = async (row) => {
    const st = (row.zapmail_status || '').toUpperCase();
    if (st !== 'ACTIVE') {
      toast({
        title: 'Mailbox not ready',
        description:
          'Connect OAuth is only available when ZapMail shows ACTIVE. Sync mailboxes on Outbound setup first.',
        status: 'warning',
        duration: 8000,
        isClosable: true,
      });
      return;
    }
    try {
      setIsOutlookConnecting(true);
      const params = {
        zapmail_mailbox_id: row.zapmail_mailbox_id,
        zapmail_domain_id: row.zapmail_domain_id,
        zapmail_workspace_id: row.workspace_id,
        return_path: '/integrations',
      };
      if (st === 'ACTIVE' && row.email) {
        params.login_hint = row.email;
      }
      const authUrl = await getOAuthAuthUrl('outlook', params);
      openOAuthInNewTab(authUrl, toast, {
        provider: 'outlook',
        accountEmail: row.email,
      });
    } catch (error) {
      toast({
        title: 'OAuth failed',
        description: error.response?.data?.detail || error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsOutlookConnecting(false);
    }
  };

  const handleConnectOutlook = async () => {
    try {
      setIsOutlookConnecting(true);
      const { data } = await api.get('/api/integrations/microsoft/auth-url');
      openOAuthInNewTab(data.auth_url, toast, { provider: 'outlook' });
    } catch (error) {
      toast({
        title: 'Connection failed',
        description: error.response?.data?.detail || 'Could not initiate Outlook connection. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsOutlookConnecting(false);
    }
  };

  const handleDisconnectOutlook = async (email) => {
    try {
      setIsOutlookConnecting(true);
      await api.delete(`/api/integrations/microsoft/disconnect?email=${encodeURIComponent(email)}`);
      setOutlookStatus(prev => ({
        accounts: (prev?.accounts ?? []).filter(a => a.email !== email),
      }));
      window.dispatchEvent(new Event('outlookIntegrationChanged'));
      toast({
        title: 'Outlook disconnected',
        description: `${email} has been unlinked.`,
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to disconnect Outlook. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsOutlookConnecting(false);
    }
  };

  if (isLoading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="xl" color="blue.400" />
        <Text mt={4} color="gray.400">Loading integrations...</Text>
      </Box>
    );
  }

  return (
    <Box px={10} pt={10} pb={16} maxW="1200px" mx="auto">
      {oauthReturnUi && (
        <OAuthTabCompleteCard
          provider={oauthReturnUi.provider}
          ok={oauthReturnUi.ok}
          accountEmail={oauthReturnUi.accountEmail}
          onDismiss={() => setOauthReturnUi(null)}
        />
      )}
      <VStack align="stretch" spacing={8}>
        {/* Header */}
        <HStack justify="space-between" align="center" maxW="700px">
          <Box>
            <HStack spacing={2.5} mb={1} align="center">
              <Heading
                size="lg"
                color="#fafafa"
                fontWeight="700"
                letterSpacing="-0.02em"
              >
                Apps
              </Heading>
              <Box
                px={2}
                py={0.5}
                borderRadius="full"
                border="1px solid rgba(255,255,255,0.12)"
                bg="rgba(255,255,255,0.05)"
              >
                <Text fontSize="10px" fontWeight="500" color="rgba(255,255,255,0.35)" letterSpacing="0.08em" textTransform="uppercase">
                  More coming soon
                </Text>
              </Box>
            </HStack>
            <Text fontSize="sm" color="rgba(255,255,255,0.35)" letterSpacing="0.01em">
              Connect your tools. All your integrations in one place.
            </Text>
          </Box>

          {/* Search bar */}
          <InputGroup w="200px" flexShrink={0}>
            <InputLeftElement pointerEvents="none" h="34px" w="34px" display="flex" alignItems="center" justifyContent="center">
              <Icon as={FiSearch} color="rgba(255,255,255,0.25)" boxSize={3.5} />
            </InputLeftElement>
            <Input
              pl={8}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search apps"
              autoComplete="off"
              bg="transparent"
              border="1px solid rgba(255,255,255,0.1)"
              borderRadius="full"
              color="rgba(255,255,255,0.75)"
              fontSize="sm"
              h="34px"
              _placeholder={{ color: 'rgba(255,255,255,0.25)' }}
              _hover={{ border: '1px solid rgba(255,255,255,0.18)' }}
              _focus={{ border: '1px solid rgba(255,255,255,0.28)', boxShadow: 'none' }}
            />
          </InputGroup>
        </HStack>

        {/* Unified integration list */}
        <Box
          maxW="700px"
          borderRadius="10px"
          border="1px solid rgba(255,255,255,0.07)"
          overflow="hidden"
        >
          {[
            {
              logo: <SmartleadLogo size="32px" />,
              name: 'Smartlead',
              description: 'AI-powered email outreach',
              isConnected: smartleadStatus?.status === 'active',
              onClick: onSmartleadDrawerOpen,
            },
            {
              logo: <HeyReachLogo size="32px" />,
              name: 'HeyReach',
              description: 'LinkedIn outreach automation',
              isConnected: heyreachStatus?.status === 'active',
              onClick: onHeyreachDrawerOpen,
            },
            {
              logo: <GmailLogo size="32px" />,
              name: 'Gmail',
              description: 'Send outreach from your own Gmail inbox',
              isConnected: (gmailStatus?.accounts?.length ?? 0) > 0,
              onClick: onGmailDrawerOpen,
            },
            {
              logo: <OutlookLogo size="32px" />,
              name: 'Outlook',
              description: 'Send outreach from your Outlook inbox',
              isConnected: (outlookStatus?.accounts?.length ?? 0) > 0,
              onClick: onOutlookDrawerOpen,
            },
          ].filter(item =>
            !searchQuery.trim() ||
            item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.description.toLowerCase().includes(searchQuery.toLowerCase())
          ).map((item, i, arr) => (
            <Box
              key={item.name}
              px={5}
              py={3.5}
              cursor="pointer"
              transition="background 0.12s ease"
              bg="transparent"
              _hover={{ bg: 'rgba(255,255,255,0.025)' }}
              borderBottom={i < arr.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none'}
              onClick={item.onClick}
            >
              <HStack justify="space-between">
                <HStack spacing={4}>
                  {item.logo}
                  <Box>
                    <HStack spacing={2} mb="2px">
                      <Text
                        fontSize="sm"
                        fontWeight="500"
                        color="rgba(255,255,255,0.88)"
                        letterSpacing="-0.01em"
                      >
                        {item.name}
                      </Text>
                      {item.isConnected && (
                        <Box
                          px={1.5}
                          py="1px"
                          borderRadius="full"
                          bg="rgba(34,197,94,0.1)"
                          border="1px solid rgba(34,197,94,0.3)"
                        >
                          <Text fontSize="9px" color="#4ade80" fontWeight="600" letterSpacing="0.06em" textTransform="uppercase">
                            Connected
                          </Text>
                        </Box>
                      )}
                    </HStack>
                    <Text fontSize="xs" color="rgba(255,255,255,0.28)" letterSpacing="0.005em">
                      {item.description}
                    </Text>
                  </Box>
                </HStack>
                <Icon as={FiChevronRight} color="rgba(255,255,255,0.18)" boxSize={4} flexShrink={0} />
              </HStack>
            </Box>
          ))}
          {/* Empty state */}
          {searchQuery.trim() && !['Smartlead','HeyReach','Gmail','Outlook'].some(n =>
            n.toLowerCase().includes(searchQuery.toLowerCase())
          ) && (
            <Box px={5} py={8} textAlign="center">
              <Text fontSize="sm" color="rgba(255,255,255,0.2)">
                No apps match &ldquo;{searchQuery}&rdquo;
              </Text>
            </Box>
          )}
        </Box>
      </VStack>

      {/* Smartlead Slide-out Drawer */}
      <Drawer
        isOpen={isSmartleadDrawerOpen}
        placement="right"
        onClose={onSmartleadDrawerClose}
        size="md"
      >
        <DrawerOverlay bg="blackAlpha.700" />
        <DrawerContent bg="#0a0a0a" borderLeft="1px solid rgba(255,255,255,0.10)">
          <DrawerCloseButton color="#a3a3a3" mt={2} _hover={{ color: '#fafafa' }} _focus={{ boxShadow: 'none', outline: 'none' }} />

          <DrawerBody px={7} pt={8} pb={8}>
            <VStack align="stretch" spacing={6}>
              <SmartleadLogo size="56px" />

              <HStack justify="space-between" align="start">
                <VStack align="start" spacing={1}>
                  <Text fontSize="2xl" fontWeight="700" color="#fafafa">Smartlead</Text>
                  <Text fontSize="sm" color="#a3a3a3">
                    AI-powered email outreach
                  </Text>
                </VStack>

                {smartleadStatus?.status === 'active' ? (
                  <Button
                    size="sm"
                    bg="transparent"
                    color="rgba(239,68,68,0.7)"
                    borderRadius="full"
                    fontWeight="500"
                    fontSize="13px"
                    px={5}
                    h="36px"
                    border="1px solid rgba(239,68,68,0.3)"
                    flexShrink={0}
                    mt={1}
                    _hover={{
                      borderColor: 'rgba(239,68,68,0.5)',
                      color: 'rgba(239,68,68,0.9)',
                      bg: 'rgba(239,68,68,0.06)',
                    }}
                    onClick={handleSmartleadDisconnect}
                    isLoading={isSmartleadConnecting}
                    loadingText="Disconnecting..."
                  >
                    Disconnect
                  </Button>
                ) : null}
              </HStack>

              {smartleadStatus?.status === 'active' ? (
                <VStack align="stretch" spacing={6}>
                  <Box p={4} borderRadius="10px" bg="#171717" border="1px solid rgba(255,255,255,0.10)">
                    <VStack align="start" spacing={2.5}>
                      {smartleadStatus?.connected_at && (
                        <HStack spacing={2}>
                          <Text fontSize="xs" color="#a3a3a3" w="60px">Since</Text>
                          <Text fontSize="xs" color="#fafafa">
                            {parseUTCDate(smartleadStatus.connected_at)?.toLocaleDateString(undefined, {
                              month: 'short', day: 'numeric', year: 'numeric'
                            })}
                          </Text>
                        </HStack>
                      )}
                      {smartleadStatus?.last_verified_at && (
                        <HStack spacing={2}>
                          <Text fontSize="xs" color="#a3a3a3" w="60px">Verified</Text>
                          <Text fontSize="xs" color="#fafafa">
                            {parseUTCDate(smartleadStatus.last_verified_at)?.toLocaleString(undefined, {
                              month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short'
                            })}
                          </Text>
                        </HStack>
                      )}
                    </VStack>
                  </Box>

                  <Button
                    size="sm"
                    variant="outline"
                    borderColor="rgba(255,255,255,0.15)"
                    color="rgba(255,255,255,0.6)"
                    bg="transparent"
                    borderRadius="full"
                    fontWeight="500"
                    fontSize="13px"
                    px={5}
                    h="36px"
                    alignSelf="start"
                    leftIcon={<Icon as={FiRefreshCw} boxSize={3.5} />}
                    _hover={{
                      borderColor: 'rgba(255,255,255,0.35)',
                      color: 'rgba(255,255,255,0.9)',
                      bg: 'rgba(255,255,255,0.04)',
                    }}
                    onClick={handleSmartleadVerify}
                    isLoading={isSmartleadConnecting}
                    loadingText="Verifying..."
                  >
                    Verify connection
                  </Button>

                  <Text fontSize="sm" color="#a3a3a3" lineHeight="1.8">
                    Launch AI-personalised email sequences at scale with Smartlead. Warm up inboxes,
                    track open and reply rates, and keep your deliverability high across every campaign.
                  </Text>

                  <Box>
                    <Text fontSize="md" fontWeight="600" color="#fafafa" mb={3}>Information</Text>
                    <Divider borderColor="rgba(255,255,255,0.10)" mb={0} />
                    <Box borderRadius="10px" border="1px solid rgba(255,255,255,0.10)" overflow="hidden">
                      {[
                        { label: 'Capabilities', value: 'Email sequences, inbox warm-up' },
                        { label: 'Developer', value: 'Smartlead' },
                        { label: 'Website', href: 'https://www.smartlead.ai' },
                        { label: 'Privacy Policy', href: 'https://www.smartlead.ai/privacy-policy' },
                      ].map((row, i, arr) => (
                        <HStack
                          key={row.label}
                          px={4}
                          py={3.5}
                          justify="space-between"
                          borderBottom={i < arr.length - 1 ? '1px solid' : 'none'}
                          borderColor="rgba(255,255,255,0.10)"
                        >
                          <Text fontSize="sm" color="#a3a3a3">{row.label}</Text>
                          {row.href ? (
                            <Link href={row.href} isExternal>
                              <Icon as={FiExternalLink} color="#a3a3a3" boxSize={4} _hover={{ color: '#fafafa' }} />
                            </Link>
                          ) : (
                            <Text fontSize="sm" fontWeight="500" color="#fafafa">{row.value}</Text>
                          )}
                        </HStack>
                      ))}
                    </Box>
                  </Box>
                </VStack>
              ) : (
                <VStack align="stretch" spacing={6}>
                  <Text fontSize="sm" color="#a3a3a3" lineHeight="1.8">
                    Launch AI-personalised email sequences at scale with Smartlead. Warm up inboxes,
                    track open and reply rates, and keep your deliverability high across every campaign.
                    Enter your API key from the Smartlead dashboard to get started.
                  </Text>

                  <VStack align="stretch" spacing={3}>
                    <FormControl>
                      <FormLabel fontSize="sm" color="#a3a3a3" mb={1.5}>API Key</FormLabel>
                      <InputGroup>
                        <Input
                          type={showSmartleadApiKey ? 'text' : 'password'}
                          placeholder="Enter your Smartlead API key"
                          value={smartleadApiKey}
                          onChange={(e) => setSmartleadApiKey(e.target.value)}
                          autoComplete="new-password"
                          name="smartlead-api-key"
                          bg="#171717"
                          borderColor="rgba(255,255,255,0.12)"
                          _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
                          _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
                          color="#fafafa"
                          fontSize="sm"
                          pr="3rem"
                          onKeyDown={(e) => e.key === 'Enter' && handleSmartleadConnect()}
                        />
                        <InputRightElement width="3rem">
                          <IconButton
                            h="1.75rem"
                            size="sm"
                            variant="ghost"
                            color="#a3a3a3"
                            _hover={{ color: '#fafafa', bg: 'transparent' }}
                            onClick={() => setShowSmartleadApiKey(!showSmartleadApiKey)}
                            icon={showSmartleadApiKey ? <FiEyeOff /> : <FiEye />}
                            aria-label={showSmartleadApiKey ? 'Hide API key' : 'Show API key'}
                          />
                        </InputRightElement>
                      </InputGroup>
                    </FormControl>

                    <HStack justify="space-between" align="center">
                      <Link
                        href="https://app.smartlead.ai/settings"
                        isExternal
                        color="rgba(255,255,255,0.4)"
                        fontSize="xs"
                        _hover={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'underline' }}
                      >
                        <HStack spacing={1}>
                          <Text>Find your API key</Text>
                          <Icon as={FiExternalLink} boxSize={3} />
                        </HStack>
                      </Link>

                      <Button
                        size="sm"
                        bg="#fafafa"
                        color="#0a0a0a"
                        borderRadius="full"
                        fontWeight="600"
                        fontSize="13px"
                        px={5}
                        h="36px"
                        _hover={{ opacity: 0.8 }}
                        onClick={handleSmartleadConnect}
                        isLoading={isSmartleadConnecting}
                        loadingText="Connecting..."
                      >
                        Connect
                      </Button>
                    </HStack>
                  </VStack>

                  <Box>
                    <Text fontSize="md" fontWeight="600" color="#fafafa" mb={3}>Information</Text>
                    <Divider borderColor="rgba(255,255,255,0.10)" mb={0} />
                    <Box borderRadius="10px" border="1px solid rgba(255,255,255,0.10)" overflow="hidden">
                      {[
                        { label: 'Capabilities', value: 'Email sequences, inbox warm-up' },
                        { label: 'Developer', value: 'Smartlead' },
                        { label: 'Website', href: 'https://www.smartlead.ai' },
                        { label: 'Privacy Policy', href: 'https://www.smartlead.ai/privacy-policy' },
                      ].map((row, i, arr) => (
                        <HStack
                          key={row.label}
                          px={4}
                          py={3.5}
                          justify="space-between"
                          borderBottom={i < arr.length - 1 ? '1px solid' : 'none'}
                          borderColor="rgba(255,255,255,0.10)"
                        >
                          <Text fontSize="sm" color="#a3a3a3">{row.label}</Text>
                          {row.href ? (
                            <Link href={row.href} isExternal>
                              <Icon as={FiExternalLink} color="#a3a3a3" boxSize={4} _hover={{ color: '#fafafa' }} />
                            </Link>
                          ) : (
                            <Text fontSize="sm" fontWeight="500" color="#fafafa">{row.value}</Text>
                          )}
                        </HStack>
                      ))}
                    </Box>
                  </Box>
                </VStack>
              )}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* HeyReach Slide-out Drawer */}
      <Drawer
        isOpen={isHeyreachDrawerOpen}
        placement="right"
        onClose={onHeyreachDrawerClose}
        size="md"
      >
        <DrawerOverlay bg="blackAlpha.700" />
        <DrawerContent bg="#0a0a0a" borderLeft="1px solid rgba(255,255,255,0.10)">
          <DrawerCloseButton color="#a3a3a3" mt={2} _hover={{ color: '#fafafa' }} _focus={{ boxShadow: 'none', outline: 'none' }} />

          <DrawerBody px={7} pt={8} pb={8}>
            <VStack align="stretch" spacing={6}>
              <HeyReachLogo size="48px" />

              <HStack justify="space-between" align="start">
                <VStack align="start" spacing={1}>
                  <Text fontSize="2xl" fontWeight="700" color="#fafafa">HeyReach</Text>
                  <Text fontSize="sm" color="#a3a3a3">
                    LinkedIn outreach automation
                  </Text>
                </VStack>

                {heyreachStatus?.status === 'active' ? (
                  <Button
                    size="sm"
                    bg="transparent"
                    color="rgba(239,68,68,0.7)"
                    borderRadius="full"
                    fontWeight="500"
                    fontSize="13px"
                    px={5}
                    h="36px"
                    border="1px solid rgba(239,68,68,0.3)"
                    flexShrink={0}
                    mt={1}
                    _hover={{
                      borderColor: 'rgba(239,68,68,0.5)',
                      color: 'rgba(239,68,68,0.9)',
                      bg: 'rgba(239,68,68,0.06)',
                    }}
                    onClick={handleHeyreachDisconnect}
                    isLoading={isHeyreachConnecting}
                    loadingText="Disconnecting..."
                  >
                    Disconnect
                  </Button>
                ) : null}
              </HStack>

              {heyreachStatus?.status === 'active' ? (
                <VStack align="stretch" spacing={6}>
                  <Box p={4} borderRadius="10px" bg="#171717" border="1px solid rgba(255,255,255,0.10)">
                    <VStack align="start" spacing={2.5}>
                      {heyreachStatus?.connected_at && (
                        <HStack spacing={2}>
                          <Text fontSize="xs" color="#a3a3a3" w="60px">Since</Text>
                          <Text fontSize="xs" color="#fafafa">
                            {parseUTCDate(heyreachStatus.connected_at)?.toLocaleDateString(undefined, {
                              month: 'short', day: 'numeric', year: 'numeric'
                            })}
                          </Text>
                        </HStack>
                      )}
                      {heyreachStatus?.last_verified_at && (
                        <HStack spacing={2}>
                          <Text fontSize="xs" color="#a3a3a3" w="60px">Verified</Text>
                          <Text fontSize="xs" color="#fafafa">
                            {parseUTCDate(heyreachStatus.last_verified_at)?.toLocaleString(undefined, {
                              month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short'
                            })}
                          </Text>
                        </HStack>
                      )}
                    </VStack>
                  </Box>

                  <Button
                    size="sm"
                    variant="outline"
                    borderColor="rgba(255,255,255,0.15)"
                    color="rgba(255,255,255,0.6)"
                    bg="transparent"
                    borderRadius="full"
                    fontWeight="500"
                    fontSize="13px"
                    px={5}
                    h="36px"
                    alignSelf="start"
                    leftIcon={<Icon as={FiRefreshCw} boxSize={3.5} />}
                    _hover={{
                      borderColor: 'rgba(255,255,255,0.35)',
                      color: 'rgba(255,255,255,0.9)',
                      bg: 'rgba(255,255,255,0.04)',
                    }}
                    onClick={handleHeyreachVerify}
                    isLoading={isHeyreachConnecting}
                    loadingText="Verifying..."
                  >
                    Verify connection
                  </Button>

                  <Text fontSize="sm" color="#a3a3a3" lineHeight="1.8">
                    Automate LinkedIn outreach at scale with HeyReach. Launch personalised sequences,
                    track reply rates, and sync prospect activity back into your campaigns.
                  </Text>

                  <Box>
                    <Text fontSize="md" fontWeight="600" color="#fafafa" mb={3}>Information</Text>
                    <Divider borderColor="rgba(255,255,255,0.10)" mb={0} />
                    <Box borderRadius="10px" border="1px solid rgba(255,255,255,0.10)" overflow="hidden">
                      {[
                        { label: 'Capabilities', value: 'LinkedIn sequences' },
                        { label: 'Developer', value: 'HeyReach' },
                        { label: 'Website', href: 'https://www.heyreach.io' },
                        { label: 'Privacy Policy', href: 'https://www.heyreach.io/privacy-policy' },
                      ].map((row, i, arr) => (
                        <HStack
                          key={row.label}
                          px={4}
                          py={3.5}
                          justify="space-between"
                          borderBottom={i < arr.length - 1 ? '1px solid' : 'none'}
                          borderColor="rgba(255,255,255,0.10)"
                        >
                          <Text fontSize="sm" color="#a3a3a3">{row.label}</Text>
                          {row.href ? (
                            <Link href={row.href} isExternal>
                              <Icon as={FiExternalLink} color="#a3a3a3" boxSize={4} _hover={{ color: '#fafafa' }} />
                            </Link>
                          ) : (
                            <Text fontSize="sm" fontWeight="500" color="#fafafa">{row.value}</Text>
                          )}
                        </HStack>
                      ))}
                    </Box>
                  </Box>
                </VStack>
              ) : (
                <VStack align="stretch" spacing={6}>
                  <Text fontSize="sm" color="#a3a3a3" lineHeight="1.8">
                    Automate LinkedIn outreach at scale with HeyReach. Launch personalised sequences,
                    track reply rates, and sync prospect activity back into your campaigns.
                    Enter your API key from the HeyReach dashboard to get started.
                  </Text>

                  <VStack align="stretch" spacing={3}>
                    <FormControl>
                      <FormLabel fontSize="sm" color="#a3a3a3" mb={1.5}>API Key</FormLabel>
                      <InputGroup>
                        <Input
                          type={showHeyreachApiKey ? 'text' : 'password'}
                          placeholder="Enter your HeyReach API key"
                          value={heyreachApiKey}
                          onChange={(e) => setHeyreachApiKey(e.target.value)}
                          autoComplete="new-password"
                          name="heyreach-api-key"
                          bg="#171717"
                          borderColor="rgba(255,255,255,0.12)"
                          _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
                          _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
                          color="#fafafa"
                          fontSize="sm"
                          pr="3rem"
                          onKeyDown={(e) => e.key === 'Enter' && handleHeyreachConnect()}
                        />
                        <InputRightElement width="3rem">
                          <IconButton
                            h="1.75rem"
                            size="sm"
                            variant="ghost"
                            color="#a3a3a3"
                            _hover={{ color: '#fafafa', bg: 'transparent' }}
                            onClick={() => setShowHeyreachApiKey(!showHeyreachApiKey)}
                            icon={showHeyreachApiKey ? <FiEyeOff /> : <FiEye />}
                            aria-label={showHeyreachApiKey ? 'Hide API key' : 'Show API key'}
                          />
                        </InputRightElement>
                      </InputGroup>
                    </FormControl>

                    <HStack justify="space-between" align="center">
                      <Link
                        href="https://app.heyreach.io/settings/api"
                        isExternal
                        color="rgba(255,255,255,0.4)"
                        fontSize="xs"
                        _hover={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'underline' }}
                      >
                        <HStack spacing={1}>
                          <Text>Find your API key</Text>
                          <Icon as={FiExternalLink} boxSize={3} />
                        </HStack>
                      </Link>

                      <Button
                        size="sm"
                        bg="#fafafa"
                        color="#0a0a0a"
                        borderRadius="full"
                        fontWeight="600"
                        fontSize="13px"
                        px={5}
                        h="36px"
                        _hover={{ opacity: 0.8 }}
                        onClick={handleHeyreachConnect}
                        isLoading={isHeyreachConnecting}
                        loadingText="Connecting..."
                      >
                        Connect
                      </Button>
                    </HStack>
                  </VStack>

                  <Box>
                    <Text fontSize="md" fontWeight="600" color="#fafafa" mb={3}>Information</Text>
                    <Divider borderColor="rgba(255,255,255,0.10)" mb={0} />
                    <Box borderRadius="10px" border="1px solid rgba(255,255,255,0.10)" overflow="hidden">
                      {[
                        { label: 'Capabilities', value: 'LinkedIn sequences' },
                        { label: 'Developer', value: 'HeyReach' },
                        { label: 'Website', href: 'https://www.heyreach.io' },
                        { label: 'Privacy Policy', href: 'https://www.heyreach.io/privacy-policy' },
                      ].map((row, i, arr) => (
                        <HStack
                          key={row.label}
                          px={4}
                          py={3.5}
                          justify="space-between"
                          borderBottom={i < arr.length - 1 ? '1px solid' : 'none'}
                          borderColor="rgba(255,255,255,0.10)"
                        >
                          <Text fontSize="sm" color="#a3a3a3">{row.label}</Text>
                          {row.href ? (
                            <Link href={row.href} isExternal>
                              <Icon as={FiExternalLink} color="#a3a3a3" boxSize={4} _hover={{ color: '#fafafa' }} />
                            </Link>
                          ) : (
                            <Text fontSize="sm" fontWeight="500" color="#fafafa">{row.value}</Text>
                          )}
                        </HStack>
                      ))}
                    </Box>
                  </Box>
                </VStack>
              )}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Gmail Slide-out Drawer */}
      <Drawer
        isOpen={isGmailDrawerOpen}
        placement="right"
        onClose={onGmailDrawerClose}
        size="md"
      >
        <DrawerOverlay bg="blackAlpha.700" />
        <DrawerContent bg="#0a0a0a" borderLeft="1px solid rgba(255,255,255,0.10)">
          <DrawerCloseButton color="#a3a3a3" mt={2} _hover={{ color: '#fafafa' }} _focus={{ boxShadow: 'none', outline: 'none' }} />

          <DrawerBody px={7} pt={8} pb={8}>
            <VStack align="stretch" spacing={6}>
              <GmailLogo size="56px" />

              <HStack justify="space-between" align="start">
                <VStack align="start" spacing={1}>
                  <Text fontSize="2xl" fontWeight="700" color="#fafafa">Gmail</Text>
                  <Text fontSize="sm" color="#a3a3a3">
                    Send outreach from your own Gmail inbox
                  </Text>
                </VStack>

                {(gmailStatus?.accounts?.length ?? 0) > 0 ? (
                  <Button
                    size="sm"
                    bg="transparent"
                    color="rgba(255,255,255,0.7)"
                    borderRadius="full"
                    fontWeight="500"
                    fontSize="13px"
                    px={4}
                    h="36px"
                    border="1px solid rgba(255,255,255,0.15)"
                    flexShrink={0}
                    mt={1}
                    leftIcon={<Icon as={FiPlus} boxSize={3.5} />}
                    _hover={{ borderColor: 'rgba(255,255,255,0.3)', color: '#fafafa', bg: 'rgba(255,255,255,0.05)' }}
                    _focus={{ boxShadow: 'none', outline: 'none' }}
                    onClick={handleConnectGmail}
                    isLoading={isGmailConnecting}
                    loadingText="Connecting..."
                  >
                    Add account
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    bg="#fafafa"
                    color="#0a0a0a"
                    borderRadius="full"
                    fontWeight="600"
                    fontSize="13px"
                    px={5}
                    h="36px"
                    flexShrink={0}
                    mt={1}
                    _hover={{ opacity: 0.8 }}
                    onClick={handleConnectGmail}
                    isLoading={isGmailConnecting}
                    loadingText="Connecting..."
                  >
                    Connect
                  </Button>
                )}
              </HStack>

              {/* One row per connected Gmail account */}
              {(gmailStatus?.accounts ?? []).map(account => (
                <ConnectedEmailAccountRow
                  key={account.email}
                  account={account}
                  logo={<GmailLogo size="18px" />}
                  provider="gmail"
                  onDisconnect={handleDisconnectGmail}
                  disconnectLoading={isGmailConnecting}
                  onGetPassword={handleGetZapmailPassword}
                  passwordLoadingEmail={zapmailPasswordLoadingEmail}
                />
              ))}

              <Text fontSize="sm" color="#a3a3a3" lineHeight="1.8">
                Send outreach emails directly from your Gmail inbox. Replies land back in your inbox,
                keeping conversations natural. Authorise once via Google OAuth — no passwords stored.
              </Text>

              <Box>
                <Text fontSize="md" fontWeight="600" color="#fafafa" mb={3}>Information</Text>
                <Divider borderColor="rgba(255,255,255,0.10)" mb={0} />
                <Box borderRadius="10px" border="1px solid rgba(255,255,255,0.10)" overflow="hidden">
                  {[
                    { label: 'Capabilities', value: 'Send & read emails' },
                    { label: 'Developer', value: 'Google' },
                    { label: 'Website', href: 'https://mail.google.com' },
                    { label: 'Privacy Policy', href: 'https://policies.google.com/privacy' },
                  ].map((row, i, arr) => (
                    <HStack
                      key={row.label}
                      px={4}
                      py={3.5}
                      justify="space-between"
                      borderBottom={i < arr.length - 1 ? '1px solid' : 'none'}
                      borderColor="rgba(255,255,255,0.10)"
                    >
                      <Text fontSize="sm" color="#a3a3a3">{row.label}</Text>
                      {row.href ? (
                        <Link href={row.href} isExternal>
                          <Icon as={FiExternalLink} color="#a3a3a3" boxSize={4} _hover={{ color: '#fafafa' }} />
                        </Link>
                      ) : (
                        <Text fontSize="sm" fontWeight="500" color="#fafafa">{row.value}</Text>
                      )}
                    </HStack>
                  ))}
                </Box>
              </Box>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Outlook Slide-out Drawer */}
      <Drawer
        isOpen={isOutlookDrawerOpen}
        placement="right"
        onClose={onOutlookDrawerClose}
        size="md"
      >
        <DrawerOverlay bg="blackAlpha.700" />
        <DrawerContent bg="#0a0a0a" borderLeft="1px solid rgba(255,255,255,0.10)">
          <DrawerCloseButton color="#a3a3a3" mt={2} _hover={{ color: '#fafafa' }} _focus={{ boxShadow: 'none', outline: 'none' }} />

          <DrawerBody px={7} pt={8} pb={8}>
            <VStack align="stretch" spacing={6}>
              <OutlookLogo size="56px" />

              <HStack justify="space-between" align="start">
                <VStack align="start" spacing={1}>
                  <Text fontSize="2xl" fontWeight="700" color="#fafafa">Outlook</Text>
                  <Text fontSize="sm" color="#a3a3a3">
                    Send outreach from your Outlook inbox
                  </Text>
                </VStack>

                {(outlookStatus?.accounts?.length ?? 0) > 0 ? (
                  <Button
                    size="sm"
                    bg="transparent"
                    color="rgba(255,255,255,0.7)"
                    borderRadius="full"
                    fontWeight="500"
                    fontSize="13px"
                    px={4}
                    h="36px"
                    border="1px solid rgba(255,255,255,0.15)"
                    flexShrink={0}
                    mt={1}
                    leftIcon={<Icon as={FiPlus} boxSize={3.5} />}
                    _hover={{ borderColor: 'rgba(255,255,255,0.3)', color: '#fafafa', bg: 'rgba(255,255,255,0.05)' }}
                    _focus={{ boxShadow: 'none', outline: 'none' }}
                    onClick={handleConnectOutlook}
                    isLoading={isOutlookConnecting}
                    loadingText="Connecting..."
                  >
                    Add account
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    bg="#fafafa"
                    color="#0a0a0a"
                    borderRadius="full"
                    fontWeight="600"
                    fontSize="13px"
                    px={5}
                    h="36px"
                    flexShrink={0}
                    mt={1}
                    _hover={{ opacity: 0.8 }}
                    onClick={handleConnectOutlook}
                    isLoading={isOutlookConnecting}
                    loadingText="Connecting..."
                  >
                    Connect
                  </Button>
                )}
              </HStack>

              {/* One row per connected Outlook account */}
              {(outlookStatus?.accounts ?? []).map(account => (
                <ConnectedEmailAccountRow
                  key={account.email}
                  account={account}
                  logo={<OutlookLogo size="18px" />}
                  provider="outlook"
                  onDisconnect={handleDisconnectOutlook}
                  disconnectLoading={isOutlookConnecting}
                  onGetPassword={handleGetZapmailPassword}
                  passwordLoadingEmail={zapmailPasswordLoadingEmail}
                />
              ))}

              {(outlookStatus?.pending_zapmail ?? []).length > 0 && (
                <Box>
                  <Text fontSize="md" fontWeight="600" color="#fafafa" mb={2}>
                    Provisioned inboxes (connect OAuth)
                  </Text>
                  <VStack align="stretch" spacing={3}>
                    {(outlookStatus.pending_zapmail ?? []).map((row) => {
                      const st = (row.zapmail_status || '').toUpperCase();
                      const isActive = st === 'ACTIVE';
                      const canConnect = isActive;
                      return (
                        <Box
                          key={row.zapmail_mailbox_id}
                          px={4}
                          py={3}
                          borderRadius="10px"
                          bg="#171717"
                          border="1px solid rgba(255,255,180,0.2)"
                        >
                          <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={2}>
                            <Box flex={1} minW="200px">
                              <Text fontSize="xs" color="#fafafa" fontFamily="mono" wordBreak="break-all">
                                {row.email}
                              </Text>
                              <HStack mt={1.5} spacing={2}>
                                <Text fontSize="xs" color="rgba(255,255,255,0.35)">
                                  ZapMail · {st || 'unknown'}
                                </Text>
                              </HStack>
                              {!isActive && (
                                <Text fontSize="xs" color="rgba(255,180,80,0.85)" mt={2}>
                                  If ZapMail already shows ACTIVE, open{' '}
                                  <Link as={RouterLink} to="/outbound-setup" color="blue.300">
                                    Outbound setup
                                  </Link>{' '}
                                  and Sync mailboxes, then connect here.
                                </Text>
                              )}
                            </Box>
                            <Button
                              size="sm"
                              bg="#fafafa"
                              color="#0a0a0a"
                              borderRadius="full"
                              fontWeight="600"
                              fontSize="13px"
                              px={5}
                              h="36px"
                              flexShrink={0}
                              _hover={{ opacity: 0.8 }}
                              onClick={() => handleConnectZapmailOutlook(row)}
                              isLoading={isOutlookConnecting}
                              isDisabled={!canConnect}
                            >
                              Connect OAuth
                            </Button>
                          </HStack>
                          <ZapmailPasswordField
                            label="Mailbox password"
                            value={row.zapmail_login_password}
                            toast={toast}
                          />
                          <ZapmailPasswordField
                            label="App password (if Microsoft asks)"
                            value={row.zapmail_app_password}
                            toast={toast}
                          />
                          {row.zapmail_totp_secret && (
                            <TotpQrCode secret={row.zapmail_totp_secret} accountEmail={row.email} />
                          )}
                          <ZapmailPasswordField
                            label="2FA secret (manual entry)"
                            value={row.zapmail_totp_secret}
                            toast={toast}
                          />
                          {row.zapmail_totp_secret && (
                            <Text fontSize="xs" color="#737373" mt={1}>
                              Use the 6-digit code from your authenticator when Microsoft asks for 2FA.
                            </Text>
                          )}
                        </Box>
                      );
                    })}
                  </VStack>
                  <Text fontSize="xs" color="#737373" mt={2}>
                    Connect OAuth opens Microsoft sign-in in a new tab—keep this page open to copy credentials.
                  </Text>
                </Box>
              )}

              <Text fontSize="sm" color="#a3a3a3" lineHeight="1.8">
                Send outreach emails directly from your Outlook inbox. Replies land back in your mailbox,
                keeping conversations natural. Authorise once via Microsoft OAuth — no passwords stored.
              </Text>

              <Box>
                <Text fontSize="md" fontWeight="600" color="#fafafa" mb={3}>Information</Text>
                <Divider borderColor="rgba(255,255,255,0.10)" mb={0} />
                <Box borderRadius="10px" border="1px solid rgba(255,255,255,0.10)" overflow="hidden">
                  {[
                    { label: 'Capabilities', value: 'Send & read emails' },
                    { label: 'Developer', value: 'Microsoft' },
                    { label: 'Website', href: 'https://outlook.com' },
                    { label: 'Privacy Policy', href: 'https://privacy.microsoft.com/privacystatement' },
                  ].map((row, i, arr) => (
                    <HStack
                      key={row.label}
                      px={4}
                      py={3.5}
                      justify="space-between"
                      borderBottom={i < arr.length - 1 ? '1px solid' : 'none'}
                      borderColor="rgba(255,255,255,0.10)"
                    >
                      <Text fontSize="sm" color="#a3a3a3">{row.label}</Text>
                      {row.href ? (
                        <Link href={row.href} isExternal>
                          <Icon as={FiExternalLink} color="#a3a3a3" boxSize={4} _hover={{ color: '#fafafa' }} />
                        </Link>
                      ) : (
                        <Text fontSize="sm" fontWeight="500" color="#fafafa">{row.value}</Text>
                      )}
                    </HStack>
                  ))}
                </Box>
              </Box>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <Modal
        isOpen={Boolean(zapmailCredsModal)}
        onClose={() => setZapmailCredsModal(null)}
        size="md"
        isCentered
      >
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="#0a0a0a" border="1px solid rgba(255,255,255,0.10)">
          <ModalHeader color="#fafafa" fontSize="md" pb={2}>
            Mailbox sign-in credentials
          </ModalHeader>
          <ModalCloseButton color="#a3a3a3" _hover={{ color: '#fafafa' }} />
          <ModalBody pb={6}>
            <Text fontSize="sm" color="#a3a3a3" mb={1}>
              Use these when Google or Microsoft asks you to sign in to this provisioned inbox—not
              your GillyReach login. Open Connect OAuth in a new tab and keep this window open to copy values.
            </Text>
            {zapmailCredsModal?.account_email && (
              <Text fontSize="xs" color="#fafafa" fontFamily="mono" mt={2}>
                {zapmailCredsModal.account_email}
              </Text>
            )}
            <ZapmailPasswordField
              label="Mailbox password"
              value={zapmailCredsModal?.login_password}
              toast={toast}
            />
            <ZapmailPasswordField
              label="App password (if Google asks)"
              value={zapmailCredsModal?.app_password}
              toast={toast}
            />
            {zapmailCredsModal?.totp_secret && (
              <TotpQrCode
                secret={zapmailCredsModal.totp_secret}
                accountEmail={zapmailCredsModal.account_email}
              />
            )}
            <ZapmailPasswordField
              label="2FA secret (manual entry)"
              value={zapmailCredsModal?.totp_secret}
              toast={toast}
            />
            {zapmailCredsModal?.totp_secret && (
              <Text fontSize="xs" color="#737373" mt={1}>
                Enter the 6-digit code from your authenticator when prompted—not this secret string.
              </Text>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

    </Box>
  );
};

export default IntegrationsPage;

